import Sugar from 'sugar';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { callLambda, getActionFromLambdaResults } from '../../../../helpers/lambda.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import Company from '../../../../models/company.js';
import CommercialRelationship from '../../../../models/commercial-relationship.js';
import { renderFormField } from '../../../../helpers/form-field.js';
import '../../../../ns-elements/ns-job-layout.js';
import { nsFormFieldStyles } from '../../../../ns-styles/index.js';
import { renderDate } from '../../../../helpers/format-render.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/relationship-review.json';
import { jobSchema } from '../../job-schema.js';

@customElement(NSCommercialRelationshipReview.is)
export default class NSCommercialRelationshipReview extends NSJobComponent {
  static get is() {
    return 'ns-commercial-relationship-review';
  }

  @state()
  private __relationship?: CommercialRelationship;

  @state()
  private __supplierCompanies: Company[] = [];

  @state()
  private __contractValue?: number;

  @state()
  private __contractCurrency?: string;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { reviewed: { type: 'hidden' } } });
  }

  static styles = [...super.styles, nsFormFieldStyles];

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__relationship = await this.loadInput('comRelationship', 'relationshipId');

      this.__contractValue = this.__relationship.contractValue;
      this.__contractCurrency = this.__relationship.contractCurrency;

      const supplierIds = this.__relationship.parties
        .filter((p) => p.partyType === 'SUPPLIER' && (p.status ?? 'ACCEPTED') === 'ACCEPTED')
        .map((p) => p.companyId);
      this.__supplierCompanies = await Promise.all(
        supplierIds.map((id) => this._db!.getById<Company>('company', id)),
      ).then((companies) => companies.filter((c): c is Company => Boolean(c)));
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the relationship');
    }
  }

  private __onContractValueInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__contractValue = target.value ? Number(target.value) : undefined;
  }

  private async __complete() {
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__relationship?.id) throw new Error('Missing required relationship');
      this.loading('Recording Review');
      const lambdaResult = await callLambda(
        'company/relationship-review-complete',
        'POST',
        this._auth.token,
        JSON.stringify({
          relationshipId: this.__relationship.id,
          contractValue: this.__contractValue,
          contractCurrency: this.__contractCurrency,
        }),
      );

      await getActionFromLambdaResults(lambdaResult, 'commercial_relationship_reviewed');
      this.loaded();
      await this.outcome('reviewed');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while recording the review');
    }
  }

  render() {
    const suppliers = this.__supplierCompanies.map((c) => Sugar.String.capitalize(c.name, true, true)).join(', ');
    const reviewDate = renderDate(this.__relationship?.reviewDate, 'longDate');

    return html`
      <ns-job-layout>
        <p slot="intro">Time to review this commercial relationship with ${suppliers}.</p>
        <div class="job-panel">
          <h3>Description</h3>
          <p>${this.__relationship?.description}</p>
          ${renderFormField({
            id: 'contractValue',
            label: `Contract value (${this.__contractCurrency || 'GBP'})`,
            control: html`
              <input
                id="contractValue"
                type="number"
                .value=${this.__contractValue?.toString() ?? ''}
                @input=${this.__onContractValueInput}
              />
            `,
          })}
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>Confirming this review pushes the next review date out six months from today (currently ${reviewDate}).</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__complete()}>Confirm Review</wa-button>
    `;
  }
}
